import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { useSquarePayments } from "../hooks/useSquarePayments";

const FORM_KEY = "ce_checkout_form";

export default function Checkout() {
  const { cart, clearCart, user, API, authHeaders, content } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState(() => {
    const saved = JSON.parse(sessionStorage.getItem(FORM_KEY) || "null");
    return saved || { customer_name: user?.name || "", customer_phone: "", notes: "", payment_method: "cash_pickup" };
  });
  const [loading, setLoading] = useState(false);
  const [payCfg, setPayCfg] = useState(null);
  const squareRef = useRef(null);
  const total = cart.reduce((s,x)=>s+x.price*x.qty,0);

  useEffect(() => { sessionStorage.setItem(FORM_KEY, JSON.stringify(form)); }, [form]);

  useEffect(() => {
    axios.get(`${API}/payments/public-config`).then(r => setPayCfg(r.data)).catch(()=>setPayCfg({square_configured:false}));
  }, [API]);

  const finish = (msg) => {
    sessionStorage.removeItem(FORM_KEY);
    clearCart();
    toast.success(msg);
    nav("/orders");
  };

  const chargeSquare = async (sourceId, verificationToken) => {
    const orderRes = await axios.post(`${API}/orders`, { ...form, items: cart }, { headers: authHeaders() });
    const charge = await axios.post(`${API}/payments/square`, {
      order_id: orderRes.data.order_id, source_id: sourceId, verification_token: verificationToken || undefined,
    }, { headers: authHeaders() });
    finish(charge.data.paid ? "Payment received! Order confirmed." : `Payment ${charge.data.status}`);
  };

  const fail = (err) => toast.error(err.response?.data?.detail || err.message || "Payment failed");

  const onCashAppToken = async ({ tokenResult, error }) => {
    if (error || tokenResult?.status !== "OK") { toast.error(error?.message || "Cash App payment was not approved"); return; }
    if (!form.customer_name || !form.customer_phone) { toast.error("Enter your name and phone first"); return; }
    setLoading(true);
    try { await chargeSquare(tokenResult.token); } catch (err) { fail(err); }
    setLoading(false);
  };

  const square = useSquarePayments({ payCfg, method: form.payment_method, total, containerRef: squareRef, onCashAppToken });

  const submit = async (e) => {
    e.preventDefault();
    if (form.payment_method === "cash_app_pay") { toast.info("Tap the Cash App Pay button to pay"); return; }
    const cashWin = form.payment_method === "cashapp_tag"
      ? window.open(`https://cash.app/$${payCfg.cashapp_cashtag}/${total.toFixed(2)}`, "_blank") : null;
    setLoading(true);
    try {
      if (form.payment_method === "square_card") {
        if (!square.instance) throw new Error(square.error || "Card form is still loading");
        const tokenResult = await square.instance.tokenize();
        if (tokenResult.status !== "OK") throw new Error(tokenResult.errors?.[0]?.message || "Card was not accepted");
        const vToken = await square.verifyBuyer(tokenResult.token, form.customer_name);
        await chargeSquare(tokenResult.token, vToken);
        return;
      }
      await axios.post(`${API}/orders`, { ...form, items: cart }, { headers: authHeaders() });
      finish(form.payment_method === "cashapp_tag"
        ? `Order placed! Send $${total.toFixed(2)} to $${payCfg.cashapp_cashtag} in Cash App.`
        : "Order placed! Pickup in Rossville.");
    } catch (err) {
      cashWin?.close();
      fail(err);
    }
    setLoading(false);
  };

  if (cart.length === 0) { nav("/shop"); return null; }

  const businessName = content?.business_name || "Condor & Eagle Trading Post";
  const contactPhone = content?.contact_phone;
  const contactEmail = content?.contact_email;
  const contactAddress = content?.contact_address;

  const onlineMethodsAvailable = !!payCfg?.square_configured;
  const cashAppAvailable = onlineMethodsAvailable && !!payCfg?.cash_app_pay_enabled;
  const cashAppTagAvailable = !!payCfg?.cashapp_cashtag;

  const methods = [
    { v: "cash_pickup", label: "Cash at Pickup", sub: "Pay cash in-store" },
    { v: "card_pickup", label: "Card at Pickup", sub: "Swipe at the register" },
    onlineMethodsAvailable && { v: "square_card", label: "Pay Online — Card", sub: "Secure card payment via Square" },
    cashAppAvailable && { v: "cash_app_pay", label: "Pay with Cash App (Square)", sub: "Embedded Cash App Pay button — auto-verified" },
    cashAppTagAvailable && { v: "cashapp_tag", label: `Send to Cash App $${payCfg.cashapp_cashtag}`, sub: `Open Cash App and send the total to ${payCfg.cashapp_display_name || '$'+payCfg.cashapp_cashtag}` },
  ].filter(Boolean);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="checkout-page">
      <h1 className="font-display text-5xl mb-8">CHECKOUT</h1>
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <label className="font-mono text-xs block mb-1">FULL NAME</label>
            <input required value={form.customer_name} onChange={e=>setForm({...form, customer_name:e.target.value})} className="input-field" data-testid="name-input"/>
          </div>
          <div>
            <label className="font-mono text-xs block mb-1">PHONE</label>
            <input required value={form.customer_phone} onChange={e=>setForm({...form, customer_phone:e.target.value})} className="input-field" data-testid="phone-input"/>
          </div>
          <div>
            <label className="font-mono text-xs block mb-2">PAYMENT METHOD</label>
            <div className="space-y-2" data-testid="payment-options">
              {methods.map(o => (
                <label key={o.v} className={`flex items-start gap-3 p-3 border-2 cursor-pointer ${form.payment_method===o.v?'border-[var(--accent)] bg-[var(--muted)]':'border-[var(--ink)]'}`} data-testid={`pm-${o.v}`}>
                  <input type="radio" name="pm" checked={form.payment_method===o.v} onChange={()=>setForm({...form, payment_method:o.v})} className="mt-1"/>
                  <div>
                    <div className="font-display">{o.label}</div>
                    <div className="font-mono text-xs text-[var(--ink-2)]">{o.sub}</div>
                  </div>
                </label>
              ))}
            </div>
            {!onlineMethodsAvailable && (
              <div className="font-mono text-[10px] text-[var(--ink-2)] mt-2">Online payments are not configured yet — contact the store owner.</div>
            )}
          </div>

          {(form.payment_method === "square_card" || form.payment_method === "cash_app_pay") && (
            <div className="card-industrial p-4" data-testid="square-payment-box">
              <div className="font-mono text-[10px] uppercase tracking-wider mb-2">
                {form.payment_method === "square_card" ? "Card details" : "Cash App Pay"}
              </div>
              <div ref={squareRef} key={form.payment_method} data-testid={form.payment_method === "square_card" ? "square-card-container" : "cash-app-pay-container"} className="min-h-[60px]"/>
              {!square.instance && !square.error && <div className="font-mono text-[10px] text-[var(--ink-2)]" data-testid="square-loading">Loading secure payment form...</div>}
              {square.error && <div className="font-mono text-xs text-red-700 mt-2" data-testid="square-error">Payment form error: {square.error.replace(/\.$/, "")}. Please choose another method or contact the store.</div>}
              {form.payment_method === "cash_app_pay" && <div className="font-mono text-[10px] text-[var(--ink-2)] mt-2">Fill in your name and phone, then tap the Cash App Pay button and approve in Cash App.</div>}
            </div>
          )}
          {form.payment_method === "cashapp_tag" && (
            <div className="card-industrial p-4 font-mono text-xs" data-testid="cashtag-instructions">
              Placing the order opens Cash App. Send <strong>${total.toFixed(2)}</strong> to <strong>${payCfg?.cashapp_cashtag}</strong> and include your name. We confirm your order once payment arrives.
            </div>
          )}

          <div>
            <label className="font-mono text-xs block mb-1">NOTES (OPTIONAL)</label>
            <textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} rows="3" className="input-field" data-testid="notes-input"/>
          </div>
          <div className="bg-[var(--muted)] border-2 border-[var(--ink)] p-4 font-mono text-sm space-y-1">
            <strong>PICKUP:</strong> {contactAddress || "Address on file with owner"}<br/>
            {contactPhone && <><strong>CALL:</strong> {contactPhone} · </>}{contactEmail && <><strong>EMAIL:</strong> {contactEmail}</>}<br/>
            <span>We'll call when your order is ready.</span>
          </div>
        </div>
        <div className="card-industrial p-6 h-fit">
          <h2 className="font-display text-2xl mb-4 border-b-2 border-[var(--ink)] pb-2">ORDER</h2>
          {cart.map(i => <div key={i.product_id} className="flex justify-between font-mono text-sm mb-2"><span>{i.name} ×{i.qty}</span><span>${(i.price*i.qty).toFixed(2)}</span></div>)}
          <div className="flex justify-between font-display text-2xl border-t-2 border-[var(--ink)] pt-3 mt-4"><span>TOTAL</span><span>${total.toFixed(2)}</span></div>
          <button disabled={loading} hidden={form.payment_method === "cash_app_pay"} className="btn-primary w-full mt-6" data-testid="place-order-btn">
            {(() => {
              if (loading) return "PROCESSING...";
              if (form.payment_method === "square_card") return `PAY $${total.toFixed(2)}`;
              if (form.payment_method === "cashapp_tag") return `OPEN CASH APP · $${total.toFixed(2)}`;
              return "PLACE ORDER";
            })()}
          </button>
          {form.payment_method === "cash_app_pay" && <div className="font-mono text-xs mt-6 text-center" data-testid="cash-app-pay-hint">Use the Cash App Pay button on the left to pay.</div>}
          <div className="font-mono text-[10px] text-[var(--ink-2)] mt-2 text-center">{businessName}</div>
        </div>
      </form>
    </div>
  );
}
